// Common types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

export enum UserRole {
  ADMIN = 'admin',
  VETERINARIAN = 'veterinarian',
  ASSISTANT = 'assistant',
  RECEPTIONIST = 'receptionist',
}

export interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  created_at: string;
}

export interface Animal {
  id: string;
  name: string;
  species: string;
  breed: string;
  date_of_birth: string;
  client_id: string;
  medical_history: string;
  created_at: string;
}

export interface Appointment {
  id: string;
  appointment_date: string;
  duration_minutes: string;
  appointment_type: AppointmentType;
  status: AppointmentStatus;
  reason: string;
  notes: string | null;
  client: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    mobile?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    postal_code?: string;
    country?: string;
  };
  animal: {
    id: string;
    name: string;
    species: string;
    breed: string;
    age?: string;
    gender?: string;
    birth_date?: string;
  };
  veterinarian?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  created_at?: string;
  updated_at?: string;
}

export enum AppointmentType {
  CONSULTATION = 'consultation',
  VACCINATION = 'vaccination',
  SURGERY = 'surgery',
  CHECKUP = 'checkup',
  EMERGENCY = 'emergency',
  FOLLOW_UP = 'follow_up',
}

export enum AppointmentStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show',
}

export interface AppointmentCreate {
  appointment_date: string;
  duration_minutes: number;
  appointment_type: AppointmentType;
  reason: string;
  notes?: string;
  client_id: string;
  animal_id: string;
  veterinarian_id: string;
}

export interface AppointmentUpdate {
  appointment_date?: string;
  duration_minutes?: number;
  appointment_type?: AppointmentType;
  status?: AppointmentStatus;
  reason?: string;
  notes?: string;
  veterinarian_id?: string;
}

export interface Consultation {
  id: string;
  appointment_id: string;
  animal_id: string;
  veterinarian_id: string;
  transcription: string;
  report: ConsultationReport;
  created_at: string;
}

export interface ConsultationReport {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

// Subscription types
export enum SubscriptionStatus {
  PENDING_TRIAL = 'pending_trial',
  TRIALING = 'trialing',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  ADMIN_GRANT = 'admin_grant',
  BANNED = 'banned',
}

export enum PlanType {
  FREE_TRIAL = 'free_trial',
  MONTHLY = 'monthly',
  ADMIN_GRANT = 'admin_grant',
}

export interface Subscription {
  id: string;
  user_id: string;
  status: SubscriptionStatus;
  plan_type: PlanType;
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  price_amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
  // computed
  is_active: boolean;
  trial_days_remaining: number | null;
}

export interface UsageStats {
  period_start: string | null;
  period_end: string | null;
  ai_consultations_count: number;
  transcription_minutes: number;
  chat_messages_count: number;
  ai_suggestions_count: number;
  documents_generated_count: number;
  client_reports_count: number;
}

// Admin types
export enum AdminActionType {
  GRANT_SUBSCRIPTION = 'grant_subscription',
  REVOKE_SUBSCRIPTION = 'revoke_subscription',
  BAN = 'ban',
  UNBAN = 'unban',
  START_TRIAL = 'start_trial',
  REVOKE_TRIAL = 'revoke_trial',
  CANCEL_SUBSCRIPTION = 'cancel_subscription',
  ADD_NOTE = 'add_note',
}

export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  is_banned: boolean;
  ban_reason: string | null;
  ban_expires_at: string | null;
  created_at: string;
  subscription: Subscription | null;
  last_active_at: string | null;
  usage: UsageStats | null;
  usage_all_time: UsageStats | null;
  total_ai_requests: number;
}

export interface AdminAction {
  id: string;
  admin_id: string | null;
  target_user_id: string;
  action_type: AdminActionType;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ActivityWindowStats {
  consultations: number;
  chat_messages: number;
  ai_suggestions: number;
  documents: number;
}

export interface AdminStats {
  total_users: number;
  active_subscriptions: number;
  trialing: number;
  pending_trial: number;
  canceled: number;
  banned: number;
  mrr_eur: number;
  activity_1d: ActivityWindowStats;
  activity_7d: ActivityWindowStats;
  activity_30d: ActivityWindowStats;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
  };
}
